import { useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import Image from 'next/image'
import LanguageSwitcher from '../components/LanguageSwitcher'
import SVGLogo from '../components/SVGLogo'
import axios from 'axios'
import { computeFingerprint } from '../utils/fingerprint'
import useRecaptchaV3 from '../hooks/useRecaptchaV3'
import { LanguageProvider, useLanguage } from '../context/LanguageContext'
import AUTH_COPY from '../locales/auth'


export async function getServerSideProps(context) {
  const INTERNAL_API = process.env.INTERNAL_API_URL || 'http://backend:8000'
  let suggestedLanguage = 'en'
  try {
    const cfCountry = context.req.headers['cf-ipcountry'] || ''
    const res = await fetch(`${INTERNAL_API}/v1/geo/`, {
      headers: { 'CF-IPCountry': cfCountry },
    })
    if (res.ok) {
      const data = await res.json()
      if (['en', 'ht', 'fr', 'es', 'pt-BR', 'zh-Hans'].includes(data.suggested_language)) {
        suggestedLanguage = data.suggested_language
      }
    }
  } catch (_) { /* geo hint is best-effort */ }
  return { props: { suggestedLanguage } }
}


export default function SignupPage({ suggestedLanguage }) {
  return (
    <LanguageProvider initialSuggestion={suggestedLanguage}>
      <Signup />
    </LanguageProvider>
  )
}


function Signup() {
  const { language } = useLanguage()
  const t = AUTH_COPY[language]?.signup || AUTH_COPY.en.signup
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    passwordConfirm: '',
    // Pre-select the user's display language so their transactional
    // emails come in the same language they're reading the form in.
    // User can still override before submitting.
    preferredLanguage: language,
  })
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()
  const [showComingSoon] = useState(false) // Sign up is now available!
  const { getRecaptchaToken } = useRecaptchaV3()

  const handleChange = (e) => {
    const { name, value } = e.target
    const updatedData = {
      ...formData,
      [name]: value
    }

    // Auto-populate username with email when email is entered
    if (name === 'email') {
      updatedData.username = value
    }

    setFormData(updatedData)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    if (formData.password !== formData.passwordConfirm) {
      setError(t.err_password_mismatch)
      setIsLoading(false)
      return
    }

    try {
      // Best-effort browser fingerprint for abuse-detection clustering.
      // Backend accepts blank when the browser can't compute one.
      const clientFingerprint = await computeFingerprint()
      const recaptcha_token = await getRecaptchaToken('signup')

      const response = await axios.post(
        '/api/auth/register/',
        {
          username: formData.username || formData.email,
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          password: formData.password,
          password_confirm: formData.passwordConfirm,
          preferred_language: formData.preferredLanguage,
          client_fingerprint: clientFingerprint,
          recaptcha_token,
        }
      )

      // Persist the JWT the register endpoint issues so /verify-email
      // can authenticate its own fetches (SMS status probe, WebSocket
      // subscribe, "Try SMS instead" send-otp). Without this the SMS
      // fallback silently stays hidden because /api/auth/sms/status/
      // returns 401 → smsEnabled=false → button never renders.
      if (response.data?.access) {
        try {
          window.localStorage.setItem('access_token', response.data.access)
          if (response.data.refresh) {
            window.localStorage.setItem('refresh_token', response.data.refresh)
          }
        } catch (_) { /* private-browsing / storage disabled — safe to skip */ }
      }

      // Redirect to verification page
      router.push('/verify-email?email=' + encodeURIComponent(formData.email))
    } catch (error) {
      if (error.response?.data) {
        // Handle validation errors
        const errors = error.response.data
        if (typeof errors === 'object') {
          const errorMessages = Object.entries(errors)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return value.join(' ')
              }
              return value
            })
            .join(' ')
          setError(errorMessages)
        } else {
          setError(t.err_generic)
        }
      } else {
        setError(t.err_generic)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg py-4 sm:py-12 px-3 sm:px-6 lg:px-8 overscroll-behavior-none relative">
      <LanguageSwitcher />
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-md p-4 sm:p-8 relative touch-action-manipulation">
          {/* Coming Soon Overlay */}
          {showComingSoon && (
            <div className="absolute inset-0 bg-white/80 dark:bg-dark-card/80 backdrop-blur-sm rounded-lg flex items-center justify-center z-50">
              <div className="text-center">
                <div className="transform rotate-[-20deg] mb-8">
                  <span className="bg-red-500 text-white px-6 py-3 text-2xl font-bold rounded-lg shadow-2xl inline-block">
                    {t.coming_soon_date}
                  </span>
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-lg font-medium">
                  {t.coming_soon_body}
                </p>
                <Link href="/login" className="mt-4 inline-block text-blue-600 dark:text-blue-400 hover:underline">
                  {t.coming_soon_back}
                </Link>
              </div>
            </div>
          )}
          <div className="flex justify-center mb-6">
            <SVGLogo 
              src="/CreoleCentric-Logo-v6-normal-blue.svg" 
              alt="CreoleCentric" 
              width={300} 
              height={300} 
              className="h-32 sm:h-48 w-auto"
            />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 text-gray-900 dark:text-dark-text">{t.h2}</h2>
          <p className="text-center text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6">
            {t.subtitle}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.label_first_name}
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base sm:text-sm min-h-[44px]"
                  autoComplete="given-name"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.label_last_name}
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base sm:text-sm min-h-[44px]"
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.label_email}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-base sm:text-sm min-h-[44px]"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck="false"
                autoCorrect="off"
                required
              />
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.label_username}
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder={t.username_placeholder}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-base sm:text-sm min-h-[44px]"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck="false"
                autoCorrect="off"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t.username_hint}
              </p>
            </div>

            <div>
              <label htmlFor="preferredLanguage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.label_language}
              </label>
              <select
                id="preferredLanguage"
                name="preferredLanguage"
                value={formData.preferredLanguage}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="en">{t.lang_option_en}</option>
                <option value="ht">{t.lang_option_ht}</option>
                <option value="fr">{t.lang_option_fr}</option>
                <option value="es">{t.lang_option_es}</option>
                <option value="pt-BR">{t.lang_option_pt}</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t.language_hint}
              </p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.label_password}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                autoComplete="new-password"
                required
                minLength={8}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t.password_hint}
              </p>
            </div>

            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.label_password_confirm}
              </label>
              <input
                type="password"
                id="passwordConfirm"
                name="passwordConfirm"
                value={formData.passwordConfirm}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                autoComplete="new-password"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 dark:bg-blue-700 text-white py-3 sm:py-2 rounded hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors min-h-[44px] touch-action-manipulation"
            >
              {isLoading ? t.submit_loading : t.submit_idle}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-dark-card text-gray-500 dark:text-gray-400">{t.divider}</span>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <button className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-4 py-3 sm:py-2 hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200 min-h-[44px] touch-action-manipulation">
                <span>{t.cta_google}</span>
              </button>
              <button className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-4 py-3 sm:py-2 hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200 min-h-[44px] touch-action-manipulation">
                <span>{t.cta_facebook}</span>
              </button>
            </div>
          </div>

          <div className="text-center mt-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t.have_account_prefix}
              <Link href="/login" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                {t.have_account_link}
              </Link>
            </p>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
            {t.terms_prefix}
            <Link href="/legal/terms" className="text-blue-600 dark:text-blue-400 hover:underline">
              {t.terms_link}
            </Link>
            {t.terms_and}
            <Link href="/legal/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">
              {t.privacy_link}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}